// FuseSimple — main class and create() factory.
//
// This is the only file a consumer interacts with directly. It
// orchestrates both adapters (basemap + operational), the sync loop,
// and the click / hover passthrough pipeline. The developer gets one
// `create()` call and a handful of events — nothing else leaks out.
//
// Lifecycle (see `docs/flows/basemap-lifecycle.md` for the full
// sequence diagram):
//
//   0. Stash consumer's layers to prevent extent override.
//   1. Validate spatial reference (Web Mercator only).
//   2. Instantiate adapters.
//   3. Strip existing basemap if present, apply transparency.
//   4. Read PMTiles header, compute LODs, apply to view constraints.
//   5. Mount basemap into the sibling div.
//   6. Inject OSM attribution.
//   7. Start the sync loop, initial sync push, restore layers,
//      wire passthrough events.
//
// `destroy()` reverses this sequence: unsubscribe sync + passthrough,
// destroy the basemap adapter, remove the sibling div.

import {
  type DuckReactiveUtils,
  type EsriAdapterOptions,
  EsriOperationalAdapter,
} from "../adapters/esri";
import { type MapLibreAdapterOptions, MapLibreBasemapAdapter } from "../adapters/maplibre";
import type { BasemapFeature, GeoScreenPoint, LngLat, OperationalAdapter } from "../adapters/types";
import { type DebugLogger, createDebugLogger } from "../debug-logger";
import { computeLODsFromHeader, zoomFromResolution } from "./lod";
import { type SplashOptions, createSplash } from "./splash";

// ---------------------------------------------------------------------------
// Public option types
// ---------------------------------------------------------------------------

export interface BasemapOptions {
  engine: "maplibre";
  /** URL or relative path to the PMTiles archive. */
  source: string;
  /** Optional MapLibre style JSON object or URL. Omit for bundled Protomaps Light. */
  style?: string | object;
  /**
   * Attribution text injected into the Esri Attribution widget.
   * Defaults to `"© OpenStreetMap"` per spec §6.2.
   */
  attribution?: string;
}

export interface OperationalOptions {
  engine: "esri";
  /**
   * Either an `@arcgis/core` `MapView` instance (classic API) **or** an
   * `<arcgis-map>` element (web component, Esri Maps SDK 5.0+). When an
   * element is passed, `FuseSimple.create()` awaits its `viewOnReady()`
   * internally before proceeding — no listener wiring or ready-race
   * handling required on the consumer side.
   *
   * Typed as `unknown` so consumer apps don't need `@arcgis/core`
   * resolvable at FuseSimple's compile time — the adapter narrows via
   * duck-type checks.
   */
  view: unknown;
}

export interface FuseSimpleOptions {
  basemap: BasemapOptions;
  operational: OperationalOptions;
  /**
   * Loading splash configuration. The splash is a neutral overlay that
   * masks the MapLibre canvas during initialization so the consumer never
   * sees an unsynced or partially-tiled basemap. It dismisses itself as
   * soon as MapLibre reaches `idle` — or after `timeout` ms as a safety
   * net. See `docs/flows/loading-splash.md`.
   *
   * Omit to accept defaults (enabled, `"FuseSimple"` label on `#f5f5f5`).
   */
  splash?: SplashOptions;
  /**
   * When `true`, the internal debug logger is hard-locked off. The
   * `?debug=` URL parameter is ignored and **all** logging is suppressed,
   * including `BUG` level. Use this in production builds where end-users
   * could otherwise hit `?debug=all` and inspect internals.
   *
   * Default: `false`.
   */
  isProduction?: boolean;
}

// ---------------------------------------------------------------------------
// Public event types
// ---------------------------------------------------------------------------

export interface BasemapClickEvent {
  /** Basemap features at the click point (empty array if none). */
  features: BasemapFeature[];
  /** Geographic coordinates of the click. */
  lngLat: LngLat;
}

export interface BasemapHoverEvent {
  /** Basemap features at the hover point (empty array if none). */
  features: BasemapFeature[];
  /** Geographic coordinates of the hover. */
  lngLat: LngLat;
}

// ---------------------------------------------------------------------------
// Event emitter internals
// ---------------------------------------------------------------------------

type EventMap = {
  "basemap-click": BasemapClickEvent;
  "basemap-hover": BasemapHoverEvent;
};

type EventName = keyof EventMap;

// ---------------------------------------------------------------------------
// Duck-type helpers for the Esri view. We reach into the view for a handful
// of operations that belong to the FuseSimple orchestrator, not the adapter
// (spatial reference check, transparency, basemap strip, LOD constraints).
// The adapter keeps its own DuckMapView internally — these are the pieces
// FuseSimple.create() needs that aren't on the adapter interface.
// ---------------------------------------------------------------------------

interface DuckSpatialReference {
  wkid: number;
}

interface DuckBasemap {
  title?: string;
  id?: string;
}

interface DuckLayerCollection {
  toArray(): unknown[];
  removeAll(): void;
  addMany(items: unknown[]): void;
}

interface DuckMap {
  basemap?: DuckBasemap | null;
  layers?: DuckLayerCollection;
}

interface DuckConstraints {
  lods?: unknown;
  snapToZoom?: boolean;
}

interface DuckViewForCreate {
  spatialReference?: DuckSpatialReference;
  map?: DuckMap;
  background?: unknown;
  center?: { longitude: number; latitude: number };
  zoom?: number;
  /**
   * Esri's current ground resolution in meters per CSS pixel. Used to
   * compute the correct MapLibre zoom at fractional levels — Esri
   * interpolates linearly between LODs while MapLibre uses exponential,
   * so the numeric `zoom` values diverge between integer LOD levels.
   */
  resolution?: number;
  size?: [number, number];
  constraints?: DuckConstraints;
  rotation?: number;
  container?: HTMLElement;
}

// ---------------------------------------------------------------------------
// Main class
// ---------------------------------------------------------------------------

/** Default OSM attribution per spec §6.2. */
const DEFAULT_ATTRIBUTION = "© OpenStreetMap";

/** Build-time version injected by Vite's `define`. */
declare const __FUSESIMPLE_VERSION__: string;
const VERSION = typeof __FUSESIMPLE_VERSION__ !== "undefined" ? __FUSESIMPLE_VERSION__ : "dev";

/**
 * FuseSimple — fuses a hidden MapLibre + PMTiles basemap under a
 * transparent ArcGIS MapView.
 *
 * ```ts
 * const fuse = await FuseSimple.create({
 *   basemap: { engine: "maplibre", source: "tiles.pmtiles" },
 *   operational: { engine: "esri", view },
 * });
 * ```
 */
export class FuseSimple {
  private readonly logger: DebugLogger;
  private readonly basemapAdapter: MapLibreBasemapAdapter;
  private readonly operationalAdapter: OperationalAdapter;

  /** Unsubscribe functions accumulated during setup. */
  private readonly teardowns: (() => void)[] = [];

  /** Listeners registered via `on()`. */
  private readonly listeners: {
    [K in EventName]: Array<(e: EventMap[K]) => void>;
  } = {
    "basemap-click": [],
    "basemap-hover": [],
  };

  private destroyed = false;

  // Private constructor — use the async `create()` factory.
  private constructor(
    basemapAdapter: MapLibreBasemapAdapter,
    operationalAdapter: OperationalAdapter,
    logger: DebugLogger,
  ) {
    this.basemapAdapter = basemapAdapter;
    this.operationalAdapter = operationalAdapter;
    this.logger = logger;
  }

  // -----------------------------------------------------------------------
  // Factory
  // -----------------------------------------------------------------------

  /**
   * Create a FuseSimple instance. Call **before** `await view.when()` so
   * LODs are applied before the view locks its spatial reference.
   *
   * Returns a `Promise` because mounting the basemap (PMTiles header
   * read + MapLibre style load) is async.
   */
  static async create(options: FuseSimpleOptions): Promise<FuseSimple> {
    const { basemap, isProduction } = options;
    // Local copy — we may rewrite `operational.view` below when resolving
    // an `<arcgis-map>` element to its underlying MapView. Never mutate
    // the caller's options object (could be frozen, or reused across
    // calls) — that's why we spread.
    let operational: OperationalOptions = { ...options.operational };

    // ---- Logger ----
    const logger = createDebugLogger({
      name: "FUSESIMPLE",
      features: [
        "LIFECYCLE",
        "HEADER",
        "STYLE",
        "SYNC",
        "PASSTHROUGH",
        "EVENTS",
        "ATTRIBUTION",
        "BUG",
      ],
      isProduction: isProduction ?? false,
    });

    logger.log("LIFECYCLE", {
      message: "create() start",
      options: { basemap: { source: basemap.source }, operational: { engine: operational.engine } },
    });

    // ---- Step -1: resolve <arcgis-map> element to its MapView ----
    // Web-component consumers can pass the element itself instead of
    // waiting for `arcgisViewReadyChange` and forwarding `mapEl.view`.
    // This eliminates a real listener race on cache-warm loads: Esri's
    // custom element can reach `ready` between its SDK registering and
    // the consumer's module attaching a listener, so the event fires to
    // zero listeners and `FuseSimple.create()` never runs.
    //
    // `viewOnReady()` is Esri's Stencil-style promise hook that chains
    // `componentOnReady()` + `view.whenReady()`. It resolves immediately
    // if the component is already ready — so there's no race, cache-warm
    // or cache-cold. Timeout is a safety net for malformed elements
    // (missing `basemap`, bad `item-id`, network-blocked portal) that
    // would otherwise hang us silently.
    if (isArcgisMapElement(operational.view)) {
      const mapEl = operational.view;
      logger.log("LIFECYCLE", { message: "resolving <arcgis-map> element via viewOnReady()" });
      await awaitArcgisMapReady(mapEl, logger);
      operational = { ...operational, view: mapEl.view };
      logger.log("LIFECYCLE", { message: "<arcgis-map> element resolved to MapView" });
    }

    const view = operational.view as DuckViewForCreate;

    // ---- Step 0: stash consumer's layers ----
    // Esri's MapView auto-fits to layer extents during initialization.
    // Layers with large extents zoom out to fit; layers with no extent
    // (empty GraphicsLayer, StreamLayer before data) default to world
    // extent. Either way the consumer's center/zoom gets overridden.
    //
    // Fix: temporarily remove all layers from the map so the view
    // initializes with the consumer's center/zoom intact. Layers are
    // restored in Step 7a after the initial sync push. This also
    // ensures widgets like Home capture the correct "home" viewpoint.
    const stashedLayers = view.map?.layers?.toArray() ?? [];
    if (stashedLayers.length > 0) {
      view.map?.layers?.removeAll();
      logger.log("LIFECYCLE", {
        message: "layers stashed to prevent extent override",
        count: stashedLayers.length,
      });
    }

    // ---- Step 1: spatial reference check ----
    const wkid = view.spatialReference?.wkid;
    if (wkid !== undefined && wkid !== 3857 && wkid !== 102100) {
      throw new Error(
        `FuseSimple v1 requires a Web Mercator MapView (WKID 3857/102100). Got WKID ${wkid}. Reconstruct your MapView with { spatialReference: { wkid: 3857 } }.`,
      );
    }
    logger.log("LIFECYCLE", {
      message: "SR check passed",
      wkid: wkid ?? "not yet set (will default)",
    });

    // ---- Step 1b: resolve reactiveUtils ----
    // Esri deprecated `view.watch()` in 4.32 in favor of
    // `reactiveUtils.watch()`. We resolve it dynamically so FuseSimple
    // never has a static `@arcgis/core` import. CDN users get it via
    // `$arcgis.import`, npm users via dynamic `import()`.
    const reactiveUtils = await resolveReactiveUtils(logger);

    // ---- Step 2: instantiate adapters ----
    const esriOpts: EsriAdapterOptions = { view: operational.view, reactiveUtils, logger };
    const operationalAdapter = new EsriOperationalAdapter(esriOpts);
    const basemapAdapter = new MapLibreBasemapAdapter({
      source: basemap.source,
      style: basemap.style as MapLibreAdapterOptions["style"],
      logger,
    });

    // ---- Step 3: strip existing basemap + apply transparency ----
    if (view.map?.basemap) {
      const stripped = view.map.basemap;
      logger.log("LIFECYCLE", {
        message: "stripping existing basemap",
        strippedBasemap: { title: stripped.title, id: stripped.id },
      });
      view.map.basemap = null;
    }

    // API-first transparency. The Esri MapView renders opaque by default;
    // setting background.color to fully transparent lets MapLibre show
    // through. CSS fallback applied afterwards just in case.
    view.background = { color: [0, 0, 0, 0] };
    if (view.container) {
      const canvases = view.container.querySelectorAll("canvas");
      for (const canvas of canvases) {
        canvas.style.background = "transparent";
      }
    }
    logger.log("LIFECYCLE", { message: "transparency applied" });

    // ---- Step 4: read header + compute LODs ----
    const header = await basemapAdapter.readHeader();
    const lods = computeLODsFromHeader(header);
    logger.log("HEADER", {
      message: "LODs computed",
      count: lods.length,
      minLevel: lods[0]?.level,
      maxLevel: lods[lods.length - 1]?.level,
    });

    // Apply LODs to the Esri view constraints. This must happen before
    // view.when() resolves per Decisions §3.
    if (view.constraints) {
      view.constraints.lods = lods;
      // Disable snap-to-zoom so fractional zooms work during animated
      // pans — MapLibre handles fractional zoom natively.
      view.constraints.snapToZoom = false;
    }

    // ---- Step 4b: initial extent bounds check ----
    if (view.center) {
      const [west, south, east, north] = header.bounds;
      const lng = view.center.longitude;
      const lat = view.center.latitude;
      if (lng < west || lng > east || lat < south || lat > north) {
        logger.log("HEADER", {
          message: "initial center outside PMTiles bounds",
          consumerView: { lng, lat, zoom: view.zoom },
          pmtilesBounds: header.bounds,
          hint: "MapLibre will render empty tiles for this extent.",
        });
      }
    }

    // ---- Step 4c: sync maxZoom to MapLibre adapter ----
    // The LOD ceiling determines the maximum zoom both engines support.
    // MapLibre defaults to maxZoom 22, so we must pass the LOD ceiling
    // explicitly via the adapter's setter (not by mutating the options
    // object, which would be fragile).
    const lodCeiling = lods[lods.length - 1]?.level;
    if (lodCeiling !== undefined) {
      basemapAdapter.setMaxZoom(lodCeiling);
    }

    // ---- Step 5: mount basemap ----
    // Insert the loading splash into the basemap container *before*
    // MapLibre mounts, so the consumer never sees an unsynced canvas
    // or partially-loaded tiles. The splash fades out when the basemap
    // reports `idle` (Step 7c below) or when the timeout safeguard fires
    // (owned by the splash module itself).
    const basemapContainer = operationalAdapter.getBasemapContainer();
    const splash = createSplash(basemapContainer, options.splash, logger);
    await basemapAdapter.mount(basemapContainer);
    logger.log("LIFECYCLE", { message: "basemap mounted" });

    // ---- Step 6: attribution ----
    const attributionText = basemap.attribution ?? DEFAULT_ATTRIBUTION;
    operationalAdapter.addAttribution(attributionText);
    operationalAdapter.addAttribution(`FuseSimple v${VERSION}`);

    // ---- Build the instance ----
    const instance = new FuseSimple(basemapAdapter, operationalAdapter, logger);

    // ---- Step 7: sync loop ----
    // Esri linearly interpolates resolution between LOD entries at
    // fractional zoom levels; MapLibre uses exponential interpolation.
    // At integer zooms both agree, but between LODs the numeric `zoom`
    // values map to different ground resolutions. We read Esri's
    // `view.resolution` (meters per CSS pixel) and solve for the
    // MapLibre zoom that produces the same resolution, keeping both
    // canvases pixel-locked at every fractional zoom.
    const unsubSync = operationalAdapter.onViewStateChange((state) => {
      const res = view.resolution;
      if (res !== undefined && res > 0) {
        const esriZoom = state.zoom;
        state.zoom = zoomFromResolution(res);
        logger.log("SYNC", {
          message: "zoom corrected from resolution",
          esriZoom,
          mlZoom: state.zoom,
          resolution: res,
        });
      }
      basemapAdapter.setViewState(state);
    });
    instance.teardowns.push(unsubSync);

    // ---- Step 7a: initial sync push ----
    // The watch callback only fires on *changes*. Push the current Esri
    // view state once so MapLibre starts at the right center/zoom/bearing
    // instead of its default world view.
    //
    // Because layers were stashed in Step 0, the view's center/zoom are
    // the consumer's original values — not corrupted by layer extents.
    if (view.center && view.zoom !== undefined) {
      const initialZoom =
        view.resolution !== undefined && view.resolution > 0
          ? zoomFromResolution(view.resolution)
          : view.zoom;
      basemapAdapter.setViewState({
        center: {
          lng: view.center.longitude,
          lat: view.center.latitude,
        },
        zoom: initialZoom,
        bearing: -(view.rotation ?? 0),
      });
      logger.log("SYNC", {
        message: "initial sync push",
        esriZoom: view.zoom,
        mlZoom: initialZoom,
        resolution: view.resolution,
      });
    }

    // ---- Step 7c: dismiss splash on idle ----
    // Subscribed *after* the initial sync push so the first `idle` event
    // fires with the basemap at the correct viewport, not its world-view
    // default. The splash module owns the timeout safety net — if idle
    // never fires, it force-removes itself. Teardown destroys the splash
    // unconditionally so a mid-init `destroy()` leaves no DOM residue.
    const unsubIdle = basemapAdapter.onceIdle(() => splash.dismiss());
    instance.teardowns.push(unsubIdle);
    instance.teardowns.push(() => splash.destroy());

    // ---- Step 7a-ii: restore stashed layers ----
    // Now that the view is initialized with the correct center/zoom and
    // MapLibre is synced, put the consumer's layers back. The view won't
    // auto-fit because it's already initialized.
    if (stashedLayers.length > 0) {
      view.map?.layers?.addMany(stashedLayers);
      logger.log("LIFECYCLE", {
        message: "layers restored",
        count: stashedLayers.length,
      });
    }

    // ---- Step 7b: resize relay ----
    // maplibre-gl-leaflet handles this via Leaflet's `resize` event;
    // we do the equivalent by watching `view.size`. The MapLibre adapter
    // has `trackResize: false`, so without this the basemap canvas would
    // remain stuck at the initial dimensions if the Esri view resizes
    // (e.g. browser window resize, sidebar toggle).
    const sizeHandle = reactiveUtils.watch(
      () => view.size,
      () => {
        const [width, height] = view.size ?? [0, 0];
        if (width > 0 && height > 0) {
          basemapAdapter.resize(Math.round(width), Math.round(height));
          logger.log("LIFECYCLE", {
            message: "resize relayed",
            width: Math.round(width),
            height: Math.round(height),
          });
        }
      },
    );
    instance.teardowns.push(() => sizeHandle.remove());

    // ---- Step 8: passthrough events ----
    const unsubClick = operationalAdapter.onPassthroughClick((point: GeoScreenPoint) => {
      const features = basemapAdapter.queryFeaturesAt({
        x: point.x,
        y: point.y,
      });
      const event: BasemapClickEvent = {
        features,
        lngLat: { lng: point.lng, lat: point.lat },
      };
      logger.log("EVENTS", {
        message: "basemap-click",
        featureCount: features.length,
        lngLat: event.lngLat,
      });
      instance.emit("basemap-click", event);
    });
    instance.teardowns.push(unsubClick);

    const unsubHover = operationalAdapter.onPassthroughHover((point: GeoScreenPoint) => {
      const features = basemapAdapter.queryFeaturesAt({
        x: point.x,
        y: point.y,
      });
      const event: BasemapHoverEvent = {
        features,
        lngLat: { lng: point.lng, lat: point.lat },
      };
      instance.emit("basemap-hover", event);
    });
    instance.teardowns.push(unsubHover);

    logger.log("LIFECYCLE", { message: "create() complete" });
    return instance;
  }

  // -----------------------------------------------------------------------
  // Event emitter
  // -----------------------------------------------------------------------

  /**
   * Register a listener for a FuseSimple event.
   *
   * ```ts
   * fuse.on("basemap-click", (e) => console.log(e.features));
   * ```
   */
  on(event: "basemap-click", handler: (e: BasemapClickEvent) => void): void;
  on(event: "basemap-hover", handler: (e: BasemapHoverEvent) => void): void;
  on(event: EventName, handler: (e: any) => void): void {
    this.listeners[event].push(handler);
  }

  /**
   * Remove a previously registered listener. Pass the exact same
   * function reference used in `on()`.
   *
   * ```ts
   * const handler = (e) => console.log(e);
   * fuse.on("basemap-click", handler);
   * fuse.off("basemap-click", handler);
   * ```
   */
  off(event: "basemap-click", handler: (e: BasemapClickEvent) => void): void;
  off(event: "basemap-hover", handler: (e: BasemapHoverEvent) => void): void;
  off(event: EventName, handler: (e: any) => void): void {
    const list = this.listeners[event];
    const idx = list.indexOf(handler);
    if (idx !== -1) {
      list.splice(idx, 1);
    }
  }

  /** Emit an event to all registered listeners. */
  private emit<K extends EventName>(event: K, data: EventMap[K]): void {
    // Snapshot so a handler calling off() mid-iteration won't skip entries.
    for (const handler of [...this.listeners[event]]) {
      try {
        handler(data);
      } catch (err) {
        this.logger.log("BUG", {
          bugId: "BUG-001",
          category: "EVENTS",
          description: `Uncaught error in "${event}" handler`,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  // -----------------------------------------------------------------------
  // Layer visibility
  // -----------------------------------------------------------------------

  /**
   * Show or hide a basemap style layer by its stylesheet ID.
   *
   * ```ts
   * // Hide the test parcels fill layer
   * fuse.setBasemapLayerVisibility("test-parcels-fill", false);
   *
   * // Show it again
   * fuse.setBasemapLayerVisibility("test-parcels-fill", true);
   * ```
   *
   * No-ops silently if the layer doesn't exist in the basemap style.
   */
  setBasemapLayerVisibility(layerId: string, visible: boolean): void {
    this.basemapAdapter.setLayerVisibility(layerId, visible);
  }

  // -----------------------------------------------------------------------
  // Teardown
  // -----------------------------------------------------------------------

  /**
   * Debug helper — returns the basemap engine's current rendered center.
   * Compare against `view.center` to measure sync drift.
   */
  _getBasemapCenter(): { lng: number; lat: number } | null {
    return this.basemapAdapter._getRenderedCenter?.() ?? null;
  }

  /**
   * Debug helper — returns the basemap engine's current rendered zoom.
   */
  _getBasemapZoom(): number | null {
    return this.basemapAdapter._getRenderedZoom?.() ?? null;
  }

  /**
   * Debug helper — returns the basemap canvas dimensions and center pixel.
   */
  _getBasemapCanvasInfo(): {
    width: number;
    height: number;
    centerX: number;
    centerY: number;
  } | null {
    return this.basemapAdapter._getCanvasInfo?.() ?? null;
  }

  /**
   * Tear down the FuseSimple instance. Unsubscribes sync and passthrough
   * watchers in reverse registration order, destroys the basemap adapter
   * (removes the MapLibre map and its container div), and clears all
   * event listeners. Safe to call multiple times; subsequent calls no-op.
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    this.logger.log("LIFECYCLE", { message: "destroy() start" });

    // Unsubscribe sync + passthrough in reverse registration order.
    for (let i = this.teardowns.length - 1; i >= 0; i--) {
      this.teardowns[i]();
    }
    this.teardowns.length = 0;

    // Destroy adapters (removes MapLibre map, basemap container div).
    this.basemapAdapter.destroy();
    this.operationalAdapter.destroy();

    // Clear listeners.
    this.listeners["basemap-click"].length = 0;
    this.listeners["basemap-hover"].length = 0;

    this.logger.log("LIFECYCLE", { message: "destroy() complete" });
  }
}

// ---------------------------------------------------------------------------
// reactiveUtils resolver
// ---------------------------------------------------------------------------

/**
 * Resolve `@arcgis/core/core/reactiveUtils` at runtime without a static
 * import. Tries the `$arcgis.import()` global first (CDN / web component
 * path), then falls back to a dynamic `import()` (npm / bundler path).
 */
async function resolveReactiveUtils(logger: DebugLogger): Promise<DuckReactiveUtils> {
  // CDN path: Esri's <script> tag registers `$arcgis` with an import() helper.
  const g = globalThis as Record<string, unknown>;
  if (g.$arcgis && typeof (g.$arcgis as Record<string, unknown>).import === "function") {
    const mod = await (g.$arcgis as { import: (id: string) => Promise<unknown> }).import(
      "esri/core/reactiveUtils",
    );
    logger.log("LIFECYCLE", { message: "reactiveUtils resolved via $arcgis.import" });
    return mod as DuckReactiveUtils;
  }

  // npm / bundler path: dynamic import resolves against the consumer's
  // node_modules. @arcgis/core is a peer dep so it's already installed.
  const mod = await import("@arcgis/core/core/reactiveUtils.js");
  logger.log("LIFECYCLE", { message: "reactiveUtils resolved via dynamic import" });
  return mod as unknown as DuckReactiveUtils;
}

// ---------------------------------------------------------------------------
// <arcgis-map> element resolution
// ---------------------------------------------------------------------------

/**
 * Minimal duck type for the `<arcgis-map>` web component we care about:
 * its async-ready hook and the resolved `view`. Duck-typed because we
 * don't have a static dependency on `@arcgis/map-components` and don't
 * want to pull its types into ours.
 */
interface DuckArcgisMapElement extends HTMLElement {
  view: unknown;
  viewOnReady: () => Promise<void>;
}

/** Default timeout for `<arcgis-map>` readiness. 15s — map SDK convention. */
const ARCGIS_MAP_READY_TIMEOUT_MS = 15_000;

/**
 * True if `v` is an `<arcgis-map>` (or compatible) web component element.
 * Checks: (a) it's an HTMLElement — a MapView instance never is; (b) it
 * carries the `viewOnReady` Stencil hook — present on `<arcgis-map>`,
 * absent on both MapView and random divs. Belt-and-suspenders because
 * a single `instanceof HTMLElement` check alone is almost-enough but
 * wouldn't catch the unlikely case of a consumer passing, say, a
 * `<div>` by mistake.
 */
function isArcgisMapElement(v: unknown): v is DuckArcgisMapElement {
  if (typeof HTMLElement === "undefined") return false;
  if (!(v instanceof HTMLElement)) return false;
  const maybe = v as { viewOnReady?: unknown };
  return typeof maybe.viewOnReady === "function";
}

/**
 * Wait for an `<arcgis-map>` element to reach ready state, racing against
 * a timeout and an early-fail on `arcgisViewReadyError`. Resolves when
 * `viewOnReady()` resolves; rejects with a diagnostic message otherwise.
 *
 * `viewOnReady()` itself is race-free — it chains `componentOnReady()`
 * (Stencil hydration) and `view.whenReady()` (Esri MapView ready), both
 * of which are promises that resolve immediately if their state is
 * already true. So this function is primarily here for the *error*
 * paths: a malformed element that never becomes ready would otherwise
 * hang `FuseSimple.create()` silently forever.
 */
async function awaitArcgisMapReady(
  mapEl: DuckArcgisMapElement,
  logger: DebugLogger,
): Promise<void> {
  let errorListener: ((e: Event) => void) | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const earlyError = new Promise<never>((_, reject) => {
    errorListener = (e: Event) => {
      // Esri surfaces underlying init failures (bad basemap, portal
      // errors, etc.) via this event. Fail fast with the real reason
      // rather than waiting out the timeout.
      const detail = (e as CustomEvent).detail;
      const msg =
        detail instanceof Error
          ? detail.message
          : typeof detail === "string"
            ? detail
            : "unknown error";
      logger.log("BUG", {
        bugId: "arcgis-view-ready-error",
        category: "LIFECYCLE",
        description: `<arcgis-map> emitted arcgisViewReadyError: ${msg}`,
      });
      reject(new Error(`FuseSimple: <arcgis-map> failed to become ready — ${msg}`));
    };
    mapEl.addEventListener("arcgisViewReadyError", errorListener);
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      logger.log("BUG", {
        bugId: "arcgis-map-ready-timeout",
        category: "LIFECYCLE",
        description: `<arcgis-map> did not become ready within ${ARCGIS_MAP_READY_TIMEOUT_MS}ms.`,
      });
      reject(
        new Error(
          `FuseSimple: <arcgis-map> did not become ready within ${ARCGIS_MAP_READY_TIMEOUT_MS}ms. ` +
            "Check the element's basemap, center, and zoom attributes, and watch for " +
            "arcgisViewReadyError in the console.",
        ),
      );
    }, ARCGIS_MAP_READY_TIMEOUT_MS);
  });

  try {
    await Promise.race([mapEl.viewOnReady(), earlyError, timeoutPromise]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
    if (errorListener !== null) mapEl.removeEventListener("arcgisViewReadyError", errorListener);
  }
}
